import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { rem } from 'polished';
import { colors, mixins } from '../../core/styles/theme/default';
import ImageWithCaption from '../Image/ImageWithCaption';
import { ImageInterface } from '../../types/ImageInterface';
import videojs, { VideoJsPlayer } from 'video.js';
import 'videojs-seek-buttons';
import 'video.js/dist/video-js.css';
import 'videojs-seek-buttons/dist/videojs-seek-buttons.css';

const videoJsOptions = {
  controls: true,
  playsinline: true,
  plugins: {
    seekButtons: {
      forward: 30,
      back: 10,
    },
  },
  controlBar: {
    fullscreenToggle: false,
    remainingTimeDisplay: false,
  },
  inactivityTimeout: 0,
  playbackRates: [0.5, 1, 1.5, 2],
};

const pulse = keyframes`
  0% {
    transform: scale(.9);
  }
  70% {
    transform: scale(1);
  }
  100% {
    transform: scale(.9);
  }
`;

const StyledPoster = styled.div`
  pointer-events: none;

  &,
  .gatsby-image-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      112deg,
      rgba(18, 56, 95, 0.55) 0%,
      rgba(13, 40, 68, 0.55) 100%
    );
  }
`;

const StyledVideoDuration = styled.div`
  position: absolute;
  bottom: ${rem(10)};
  right: ${rem(10)};
  border-radius: ${rem(3)};
  line-height: 1;
  font-size: ${rem(16)};
  background: rgba(0, 0, 0, 0.8);
  padding: ${rem(5)};
  z-index: 1;
`;

const StyledPodcast = styled.div`
  position: relative;

  .video-js {
    padding-top: 0;
    width: 100%;
    background: linear-gradient(
      110deg,
      ${colors.nightBlue700} 0%,
      ${colors.nightBlue900} 100%
    );

    ${mixins.responsiveStyles({
      height: {
        zero: 250,
        small: 320,
        medium: 420,
        large: 450,
        wide: 450,
        ultra: 480,
      },
    })}
  }

  .vjs-control-bar {
    flex-wrap: wrap;
    height: ${rem(80)};
    padding: ${rem(10)};
    background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.6));

    ${mixins.MQMax('small')} {
      height: ${rem(100)};
    }

    .vjs-control {
      height: ${rem(40)};

      .vjs-icon-placeholder:before {
        font-size: ${rem(20)};
        line-height: ${rem(40)};
      }

      &.vjs-play-control > .vjs-icon-placeholder:before {
        font-size: ${rem(25)};
      }
    }

    .vjs-progress-control {
      flex: 0 0 100%;
      order: -1;
      height: ${rem(20)};

      .vjs-progress-holder {
        margin: 0 ${rem(12)};
        height: ${rem(4)};
        border-radius: ${rem(2)};
        transition: none;
      }

      .vjs-play-progress,
      .vjs-load-progress,
      .vjs-load-progress div {
        border-radius: inherit;
      }

      .vjs-play-progress::before {
        font-size: ${rem(16)};
        right: ${rem(-8)};
        top: ${rem(-6)};
      }

      .vjs-time-tooltip {
        font-size: ${rem(12)} !important;
        padding: ${rem(8)};
        border-radius: ${rem(3)};
      }
    }

    .vjs-volume-control {
      height: inherit !important;
      width: ${rem(60)} !important;

      .vjs-volume-bar {
        margin: ${rem(18)} ${rem(5)};
      }

      .vjs-volume-tooltip {
        padding: ${rem(8)};
      }
    }

    .vjs-time-control {
      display: block;
      font-size: ${rem(12)};
      line-height: ${rem(40)};
      z-index: 1;

      ${mixins.MQMax('small')} {
        z-index: unset;
        order: -2;
        height: ${rem(20)};
        line-height: ${rem(20)};
      }

      &.vjs-time-divider {
        padding: 0;
        min-width: 0;
      }

      &.vjs-current-time {
        padding-right: ${rem(4)};

        ${mixins.MQMax('small')} {
          margin-left: auto;
        }
      }

      &.vjs-duration {
        padding-left: ${rem(4)};
      }
    }

    .vjs-playback-rate {
      margin-left: auto;
      z-index: 1;

      .vjs-playback-rate-value {
        font-size: ${rem(12)};
        line-height: ${rem(40)};
      }

      .vjs-menu-content {
        background: rgba(0, 0, 0, 0.8);
        border-radius: ${rem(3)};
      }
    }
  }

  .vjs-has-started ${StyledVideoDuration} {
    display: none;
  }

  .vjs-big-play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    cursor: pointer;
    padding: 0;
    margin: ${rem(-40)} 0 0 ${rem(-40)};
    width: ${rem(80)};
    height: ${rem(80)};
    border-radius: 50%;
    border: none;
    background-color: ${colors.white};
    box-shadow: 0 ${rem(2)} ${rem(4)} rgba(0, 0, 0, 0.06),
      0 ${rem(4)} ${rem(6)} rgba(0, 0, 0, 0.1);
    animation: ${pulse} 1.5s infinite;

    .vjs-icon-placeholder::before {
      content: '';
      position: absolute;
      top: 50%;
      left: calc(50% + ${rem(1)});
      transform: translate(-50%, -50%);
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cdefs%3E%3ClinearGradient id='a' x1='16.67' y1='6.01' x2='5.23' y2='-15.86' gradientTransform='matrix(1 0 0 -1 0 6)' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23faa829'/%3E%3Cstop offset='1' stop-color='%23ff3f00'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M6.5 3.87 18.7 12 6.5 20.13Z' fill='none' stroke-width='2' stroke='url(%23a)'/%3E%3C/svg%3E")
        no-repeat center / contain;
      width: ${rem(24)};
      height: ${rem(24)};
    }
  }

  .video-js:hover .vjs-big-play-button {
    background-color: ${colors.white};
    animation-play-state: paused;
  }

  .vjs-text-track-display {
    bottom: ${rem(80)};

    ${mixins.MQMax('small')} {
      bottom: ${rem(100)};
    }
  }
`;

export const Podcast = ({
  title,
  url,
  subtitles,
  poster,
  ...rest
}: PodcastProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const [videoDuration, setVideoDuration] = useState<string | null>(null);

  useEffect(() => {
    if (!playerRef.current) {
      const videoElement = videoRef.current;

      if (!videoElement) return;

      const options = {
        ...videoJsOptions,
        sources: [{ src: url }],
        ...(subtitles && {
          tracks: [{ src: subtitles, default: true, label: 'default' }],
        }),
      };
      const player = (playerRef.current = videojs(videoElement, options));

      player.on(['loadstart', 'loadedmetadata', 'durationchange'], () => {
        const duration = player.duration();

        if (!isNaN(duration) && isFinite(duration) && duration > 0) {
          setVideoDuration(videojs.formatTime(duration, 1000));
        }
      });
    }
  }, [videoRef, url, subtitles]);

  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <StyledPodcast {...rest}>
      <div data-vjs-player>
        <video ref={videoRef} title={title} className="video-js" />
        {poster && (
          <StyledPoster>
            <ImageWithCaption data={poster} />
          </StyledPoster>
        )}
        {videoDuration && (
          <StyledVideoDuration>{videoDuration}</StyledVideoDuration>
        )}
      </div>
    </StyledPodcast>
  );
};

export type PodcastProps = {
  title: string;
  url: string;
  subtitles?: string;
  poster?: ImageInterface;
};

export default Podcast;
